import { useState, useEffect, useCallback } from 'react';
import API from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
    Flame, Thermometer, Snowflake, MapPin, DollarSign, Clock,
    Send, MessageSquare, Mail, Phone, Zap, RefreshCw, Search,
    ChevronRight, X, Edit3, CheckCircle, AlertCircle, User,
    BarChart3, Target, ArrowRight, Download, Crosshair
} from 'lucide-react';

const CATEGORY_STYLE = {
    HOT: { bg: 'bg-red-50 border-red-300', badge: 'bg-red-500 text-white', icon: Flame, text: 'text-red-700' },
    WARM: { bg: 'bg-amber-50 border-amber-300', badge: 'bg-amber-500 text-white', icon: Thermometer, text: 'text-amber-700' },
    COLD: { bg: 'bg-blue-50 border-blue-300', badge: 'bg-blue-400 text-white', icon: Snowflake, text: 'text-blue-600' },
};

const PIPELINE_COLORS = {
    new: 'bg-zinc-100 text-zinc-600',
    contacted: 'bg-blue-100 text-blue-700',
    interested: 'bg-emerald-100 text-emerald-700',
    matched: 'bg-purple-100 text-purple-700',
    structured: 'bg-amber-100 text-amber-700',
    signed: 'bg-teal-100 text-teal-700',
    closed: 'bg-emerald-200 text-emerald-800',
    dead: 'bg-zinc-200 text-zinc-500',
};

export default function OpportunityFeedPage() {
    const [feed, setFeed] = useState([]);
    const [counts, setCounts] = useState({});
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [ingesting, setIngesting] = useState(false);
    const [ingestForm, setIngestForm] = useState({ city: 'Houston', state: 'TX', min_dom: 90, limit: 20 });
    const [selected, setSelected] = useState(null);

    const loadFeed = useCallback(async () => {
        try {
            const [feedRes, statsRes] = await Promise.all([
                API.get(`/opportunities/feed?category=${filter}`),
                API.get('/opportunities/stats/summary'),
            ]);
            setFeed(feedRes.data.opportunities);
            setCounts(feedRes.data.counts);
            setStats(statsRes.data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, [filter]);

    useEffect(() => { loadFeed(); }, [loadFeed]);

    const runIngestion = async () => {
        setIngesting(true);
        try {
            // Use public search endpoint first (auto-fetches from RentCast if needed)
            const { data } = await API.post('/opportunities/search', {
                city: ingestForm.city,
                state: ingestForm.state,
            });
            if (data.opportunities?.length > 0) {
                setFeed(data.opportunities);
                setCounts(prev => ({ ...prev, total: data.total }));
            }
            // Also try admin ingestion for higher limits
            try {
                await API.post('/opportunities/ingest', ingestForm);
            } catch {}
            await loadFeed();
        } catch (err) { console.error(err); }
        finally { setIngesting(false); }
    };

    const filtered = feed.filter(o =>
        !searchTerm || o.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        o.city?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) return (
        <div className="min-h-screen bg-[#F5F5F4] flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
        </div>
    );

    return (
        <div className="min-h-screen bg-[#F5F5F4]" data-testid="opportunity-feed-page">
            <Navbar />

            {/* Header */}
            <div className="bg-zinc-900 pt-20 pb-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h1 className="text-2xl font-semibold text-white font-['Outfit'] tracking-wide" data-testid="opp-feed-title">
                                <Flame className="w-6 h-6 inline mr-2 text-red-400" />
                                Opportunity Feed
                            </h1>
                            <p className="text-sm text-zinc-400 mt-0.5">Live scored listings — find motivated sellers automatically</p>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2 bg-zinc-800 rounded-lg px-3 py-2">
                                <Input placeholder="City" value={ingestForm.city}
                                    onChange={e => setIngestForm(f => ({ ...f, city: e.target.value }))}
                                    className="w-28 h-7 text-xs bg-zinc-700 border-zinc-600 text-white" data-testid="ingest-city" />
                                <Input placeholder="ST" value={ingestForm.state}
                                    onChange={e => setIngestForm(f => ({ ...f, state: e.target.value }))}
                                    className="w-14 h-7 text-xs bg-zinc-700 border-zinc-600 text-white" data-testid="ingest-state" />
                                <Button size="sm" onClick={runIngestion} disabled={ingesting}
                                    className="bg-red-500 hover:bg-red-600 text-white text-xs h-7 rounded-lg" data-testid="ingest-btn">
                                    {ingesting ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3 mr-1" />}
                                    {ingesting ? '' : 'Search'}
                                </Button>
                            </div>
                        </div>
                    </div>

                    {/* Stats bar */}
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                        <StatPill label="Total" value={counts.total || 0} color="zinc" />
                        <StatPill label="HOT" value={counts.hot || 0} color="red" />
                        <StatPill label="WARM" value={counts.warm || 0} color="amber" />
                        <StatPill label="COLD" value={counts.cold || 0} color="blue" />
                        <StatPill label="Contacted" value={stats?.contacted || 0} color="emerald" />
                        <StatPill label="Skip Traced" value={stats?.skip_traced || 0} color="violet" />
                    </div>
                </div>
            </div>

            {/* Filter + Search */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6 pb-2">
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex bg-white border border-zinc-200 rounded-xl p-1 shadow-sm">
                        {['all', 'HOT', 'WARM', 'COLD'].map(f => (
                            <button key={f} onClick={() => setFilter(f)}
                                className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-colors ${filter === f ? 'bg-zinc-900 text-white' : 'text-zinc-500 hover:bg-zinc-50'}`}
                                data-testid={`filter-${f.toLowerCase()}`}>
                                {f === 'all' ? 'All' : f}
                            </button>
                        ))}
                    </div>
                    <div className="relative flex-1 max-w-xs">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                        <Input placeholder="Search address or city..." value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="pl-9 h-9 border-zinc-200 text-sm" data-testid="opp-search" />
                    </div>
                    <span className="text-xs text-zinc-400">{filtered.length} opportunities</span>
                </div>
            </div>

            {/* Feed */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-12">
                {filtered.length === 0 ? (
                    <div className="bg-white rounded-2xl border border-zinc-200 p-12 text-center shadow-sm mt-4">
                        <Target className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-zinc-900 font-['Outfit'] mb-2">Search for Opportunities</h3>
                        <p className="text-sm text-zinc-500 mb-6">Enter a city and state above to find motivated sellers with properties on the market 60+ days.</p>
                        <div className="flex items-center justify-center gap-2">
                            <Input placeholder="City" value={ingestForm.city}
                                onChange={e => setIngestForm(f => ({ ...f, city: e.target.value }))}
                                className="w-40 h-9 text-sm border-zinc-200" />
                            <Input placeholder="ST" value={ingestForm.state}
                                onChange={e => setIngestForm(f => ({ ...f, state: e.target.value }))}
                                className="w-16 h-9 text-sm border-zinc-200" maxLength={2} />
                            <Button onClick={runIngestion} disabled={ingesting}
                                className="bg-red-500 hover:bg-red-600 text-white text-sm h-9 rounded-lg px-6">
                                {ingesting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-2" /> Find Properties</>}
                            </Button>
                        </div>
                        <p className="text-sm text-zinc-500">Pull listings from RentCast using the controls above to start building your pipeline.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                        {filtered.map(opp => (
                            <OpportunityCard key={opp.id} opp={opp} onClick={() => setSelected(opp)} />
                        ))}
                    </div>
                )}
            </div>

            {/* Detail/Outreach Panel */}
            {selected && (
                <OpportunityDetail opp={selected} onClose={() => { setSelected(null); loadFeed(); }} />
            )}
        </div>
    );
}

function StatPill({ label, value, color }) {
    const colors = {
        zinc: 'bg-zinc-800 text-zinc-300', red: 'bg-red-900/60 text-red-300',
        amber: 'bg-amber-900/60 text-amber-300', blue: 'bg-blue-900/60 text-blue-300',
        emerald: 'bg-emerald-900/60 text-emerald-300', violet: 'bg-violet-900/60 text-violet-300',
    };
    return (
        <div className={`${colors[color]} rounded-xl px-4 py-3 text-center`}>
            <div className="text-lg font-semibold font-['Outfit']">{value}</div>
            <div className="text-[10px] opacity-70">{label}</div>
        </div>
    );
}

function OpportunityCard({ opp, onClick }) {
    const style = CATEGORY_STYLE[opp.category] || CATEGORY_STYLE.COLD;
    const Icon = style.icon;
    return (
        <div onClick={onClick}
            className={`${style.bg} border rounded-xl p-5 cursor-pointer hover:shadow-md transition-all group`}
            data-testid={`opp-card-${opp.id}`}>
            <div className="flex items-center justify-between mb-3">
                <Badge className={`${style.badge} text-xs`}>
                    <Icon className="w-3 h-3 mr-1" /> {opp.motivation_score}pts
                </Badge>
                <div className="flex items-center gap-1.5">
                    <Badge className={PIPELINE_COLORS[opp.pipeline_status] || 'bg-zinc-100 text-zinc-600'}>
                        {opp.pipeline_status}
                    </Badge>
                    {opp.skip_traced && <Phone className="w-3 h-3 text-emerald-500" />}
                </div>
            </div>

            <div className="flex items-start gap-2 mb-2">
                <MapPin className="w-4 h-4 text-zinc-400 mt-0.5 shrink-0" />
                <div>
                    <div className="text-sm font-medium text-zinc-900 leading-tight">{opp.address}</div>
                    <div className="text-xs text-zinc-500">{opp.city}, {opp.state} {opp.zip}</div>
                </div>
            </div>

            <div className="grid grid-cols-3 gap-2 text-xs mt-3">
                <div className="text-center">
                    <DollarSign className="w-3 h-3 mx-auto text-zinc-400 mb-0.5" />
                    <div className="font-semibold text-zinc-900">${(opp.price || 0).toLocaleString()}</div>
                </div>
                <div className="text-center">
                    <Clock className="w-3 h-3 mx-auto text-zinc-400 mb-0.5" />
                    <div className="font-semibold text-zinc-900">{opp.daysOnMarket}d</div>
                </div>
                <div className="text-center">
                    <div className={`text-xs font-bold ${style.text}`}>{opp.category}</div>
                </div>
            </div>

            {opp.outreach_status !== 'none' && (
                <div className="mt-3 flex items-center gap-1.5 text-[10px] text-zinc-500">
                    <Send className="w-3 h-3" />
                    Outreach: {opp.outreach_status}
                </div>
            )}

            <div className="mt-3 text-xs text-zinc-400 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                Click to manage <ChevronRight className="w-3 h-3" />
            </div>
        </div>
    );
}

function OpportunityDetail({ opp, onClose }) {
    const [data, setData] = useState(opp);
    const [outreach, setOutreach] = useState([]);
    const [generating, setGenerating] = useState(false);
    const [editContact, setEditContact] = useState(false);
    const [contact, setContact] = useState({ contact_name: opp.contact_name || '', contact_phone: opp.contact_phone || '', contact_email: opp.contact_email || '' });
    const [sending, setSending] = useState(null);
    const [editStep, setEditStep] = useState(null);
    const [editMsg, setEditMsg] = useState('');
    const [tracing, setTracing] = useState(false);
    const [calling, setCalling] = useState(false);
    const [smsOpen, setSmsOpen] = useState(false);
    const [smsMsg, setSmsMsg] = useState('');
    const [sendingSms, setSendingSms] = useState(false);

    useEffect(() => {
        API.get(`/opportunities/${opp.id}/outreach`).then(r => setOutreach(r.data)).catch(() => {});
    }, [opp.id]);

    const generateOutreach = async () => {
        setGenerating(true);
        try {
            const { data: d } = await API.post(`/opportunities/${opp.id}/generate-outreach`);
            setOutreach(d.sequence);
        } catch (err) { console.error(err); }
        finally { setGenerating(false); }
    };

    const saveContact = async () => {
        try {
            await API.put(`/opportunities/${opp.id}/contact`, contact);
            setData(d => ({ ...d, ...contact, skip_traced: !!(contact.contact_phone || contact.contact_email) }));
            setEditContact(false);
        } catch (err) { console.error(err); }
    };

    const runSkipTrace = async () => {
        setTracing(true);
        try {
            const { data: result } = await API.post(`/opportunities/${opp.id}/skip-trace`);
            if (result.success && result.contact) {
                setData(d => ({ ...d, ...result.contact, skip_traced: true }));
                setContact(c => ({ ...c, ...result.contact }));
            } else {
                alert('No results found. Try adding contact info manually.');
            }
        } catch (err) {
            alert(err.response?.data?.detail || 'Skip trace failed');
        }
        finally { setTracing(false); }
    };

    const quickCall = async () => {
        if (!data.contact_phone) return alert('No phone number. Run skip trace first.');
        setCalling(true);
        try {
            await API.post('/signalwire/call', {
                to: data.contact_phone,
                contact_name: data.contact_name || 'Unknown',
                opportunity_id: opp.id,
            });
            alert('Call initiated!');
        } catch (err) {
            alert(err.response?.data?.detail || 'Call failed');
        }
        finally { setCalling(false); }
    };

    const quickSms = async () => {
        if (!data.contact_phone) return alert('No phone number. Run skip trace first.');
        if (!smsMsg.trim()) return;
        setSendingSms(true);
        try {
            await API.post('/signalwire/sms', {
                to: data.contact_phone,
                message: smsMsg,
                contact_name: data.contact_name || '',
                opportunity_id: opp.id,
            });
            setSmsMsg('');
            setSmsOpen(false);
            alert('SMS sent!');
        } catch (err) {
            alert(err.response?.data?.detail || 'SMS failed');
        }
        finally { setSendingSms(false); }
    };

    const updatePipeline = async (status) => {
        try {
            await API.put(`/opportunities/${opp.id}/pipeline`, { status });
            setData(d => ({ ...d, pipeline_status: status }));
        } catch (err) { console.error(err); }
    };

    const sendStep = async (stepId) => {
        setSending(stepId);
        try {
            await API.post(`/opportunities/outreach/${stepId}/send`);
            setOutreach(prev => prev.map(s => s.id === stepId ? { ...s, status: 'sent', sent_at: new Date().toISOString() } : s));
        } catch (err) {
            alert(err.response?.data?.detail || 'Send failed — add contact info first');
        }
        finally { setSending(null); }
    };

    const saveEdit = async (stepId) => {
        try {
            await API.put(`/opportunities/outreach/${stepId}`, { message: editMsg });
            setOutreach(prev => prev.map(s => s.id === stepId ? { ...s, message: editMsg } : s));
            setEditStep(null);
        } catch (err) { console.error(err); }
    };

    const style = CATEGORY_STYLE[data.category] || CATEGORY_STYLE.COLD;
    const factors = data.factors || {};

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex justify-end" onClick={onClose}>
            <div className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-testid="opp-detail-panel">
                {/* Header */}
                <div className="bg-zinc-900 p-6 sticky top-0 z-10">
                    <div className="flex items-center justify-between mb-3">
                        <Badge className={`${style.badge} text-sm px-3 py-1`}>
                            {data.category} — {data.motivation_score}pts
                        </Badge>
                        <button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
                    </div>
                    <h2 className="text-lg font-semibold text-white font-['Outfit']">{data.address}</h2>
                    <div className="text-sm text-zinc-400 mt-1">{data.city}, {data.state} {data.zip} | {data.property_type}</div>
                    <div className="flex items-center gap-4 mt-3 text-sm text-zinc-300">
                        <span><DollarSign className="w-3.5 h-3.5 inline" /> ${(data.price || 0).toLocaleString()}</span>
                        <span><Clock className="w-3.5 h-3.5 inline" /> {data.daysOnMarket} DOM</span>
                        {data.bedrooms && <span>{data.bedrooms}bd / {data.bathrooms || '?'}ba</span>}
                        {data.sqft && <span>{data.sqft?.toLocaleString()} sqft</span>}
                    </div>
                </div>

                <div className="p-6 space-y-6">
                    {/* Motivation Breakdown */}
                    <div>
                        <h3 className="text-sm font-medium text-zinc-700 mb-3 font-['Outfit']">Motivation Breakdown</h3>
                        <div className="grid grid-cols-2 gap-3">
                            <FactorBar label="Days on Market" score={factors.dom?.score} detail={`${factors.dom?.value || 0} days`} weight="40%" />
                            <FactorBar label="Price Drops" score={factors.price_drops?.score} detail={`${factors.price_drops?.count || 0} drops`} weight="30%" />
                            <FactorBar label="Relist Signals" score={factors.relists?.score} detail={`${factors.relists?.count || 0} relists`} weight="20%" />
                            <FactorBar label="Staleness" score={factors.staleness?.score} detail={`${factors.staleness?.days_since_active || 0}d inactive`} weight="10%" />
                        </div>
                    </div>

                    {/* Pipeline Status */}
                    <div>
                        <h3 className="text-sm font-medium text-zinc-700 mb-2 font-['Outfit']">Pipeline</h3>
                        <div className="flex flex-wrap gap-1.5">
                            {['new', 'contacted', 'interested', 'matched', 'structured', 'signed', 'closed', 'dead'].map(s => (
                                <button key={s} onClick={() => updatePipeline(s)}
                                    className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${data.pipeline_status === s ? PIPELINE_COLORS[s] + ' ring-2 ring-offset-1 ring-zinc-400' : 'bg-zinc-50 text-zinc-400 hover:bg-zinc-100'}`}
                                    data-testid={`pipeline-${s}`}>
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Contact / Skip Trace */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-sm font-medium text-zinc-700 font-['Outfit']">Contact Info</h3>
                            <div className="flex items-center gap-2">
                                <Button size="sm" variant="outline" onClick={runSkipTrace} disabled={tracing}
                                    className="text-xs h-6 border-violet-200 text-violet-700 hover:bg-violet-50" data-testid="skip-trace-btn">
                                    {tracing ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : <Crosshair className="w-3 h-3 mr-1" />}
                                    {tracing ? 'Tracing...' : 'Skip Trace'}
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setEditContact(!editContact)} className="text-xs h-6" data-testid="edit-contact-btn">
                                    <Edit3 className="w-3 h-3 mr-1" /> {editContact ? 'Cancel' : 'Manual'}
                                </Button>
                            </div>
                        </div>
                        {editContact ? (
                            <div className="space-y-2">
                                <Input placeholder="Name" value={contact.contact_name} onChange={e => setContact(c => ({ ...c, contact_name: e.target.value }))} className="h-8 text-sm" data-testid="contact-name-input" />
                                <Input placeholder="Phone" value={contact.contact_phone} onChange={e => setContact(c => ({ ...c, contact_phone: e.target.value }))} className="h-8 text-sm" data-testid="contact-phone-input" />
                                <Input placeholder="Email" value={contact.contact_email} onChange={e => setContact(c => ({ ...c, contact_email: e.target.value }))} className="h-8 text-sm" data-testid="contact-email-input" />
                                <Button size="sm" onClick={saveContact} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" data-testid="save-contact-btn">Save</Button>
                            </div>
                        ) : (
                            <div className="bg-zinc-50 rounded-lg p-3 space-y-1 text-sm">
                                <div className="flex items-center gap-2"><User className="w-3.5 h-3.5 text-zinc-400" /> {data.contact_name || <span className="text-zinc-400 italic">No name — run skip trace</span>}</div>
                                <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-zinc-400" /> {data.contact_phone || <span className="text-zinc-400 italic">No phone</span>}</div>
                                <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-zinc-400" /> {data.contact_email || <span className="text-zinc-400 italic">No email</span>}</div>
                                {data.contact_age && <div className="text-xs text-zinc-500">Age: {data.contact_age}</div>}
                                {data.contact_location && <div className="text-xs text-zinc-500">Lives in: {data.contact_location}</div>}
                                {data.contact_related && <div className="text-xs text-zinc-500 truncate">Related to: {data.contact_related}</div>}
                                {data.phones_found && data.phones_found.length > 1 && (
                                    <div className="text-xs text-zinc-500 mt-1">Other phones: {data.phones_found.slice(1).join(', ')}</div>
                                )}
                                {data.emails_found && data.emails_found.length > 1 && (
                                    <div className="text-xs text-zinc-500">Other emails: {data.emails_found.slice(1).join(', ')}</div>
                                )}
                                {data.skip_trace_sources && data.skip_trace_sources.length > 0 && (
                                    <div className="text-xs text-zinc-400 mt-1">Sources: {data.skip_trace_sources.map(s => s.title).join(', ')}</div>
                                )}
                                {data.skip_traced && <Badge className="bg-emerald-100 text-emerald-700 text-[10px] mt-1">Skip Traced</Badge>}

                                {/* Quick Call & SMS buttons */}
                                {data.contact_phone && (
                                    <div className="flex items-center gap-2 mt-2 pt-2 border-t border-zinc-200">
                                        <Button size="sm" onClick={quickCall} disabled={calling}
                                            className="bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] h-6 px-2" data-testid="quick-call-btn">
                                            <Phone className="w-3 h-3 mr-0.5" /> {calling ? 'Calling...' : 'Call'}
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => setSmsOpen(!smsOpen)}
                                            className="text-[10px] h-6 px-2 border-blue-200 text-blue-700 hover:bg-blue-50" data-testid="quick-sms-btn">
                                            <MessageSquare className="w-3 h-3 mr-0.5" /> SMS
                                        </Button>
                                    </div>
                                )}
                                {smsOpen && data.contact_phone && (
                                    <div className="mt-2 space-y-2">
                                        <Textarea value={smsMsg} onChange={e => setSmsMsg(e.target.value)}
                                            placeholder="Type SMS message..." className="text-xs min-h-[50px]" data-testid="quick-sms-input" />
                                        <div className="flex items-center gap-2">
                                            <Button size="sm" onClick={quickSms} disabled={sendingSms || !smsMsg.trim()}
                                                className="bg-blue-600 text-white text-[10px] h-6 px-2" data-testid="quick-sms-send">
                                                <Send className="w-3 h-3 mr-0.5" /> {sendingSms ? 'Sending...' : 'Send'}
                                            </Button>
                                            <span className="text-[10px] text-zinc-400">{smsMsg.length}/160</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* AI Outreach */}
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-medium text-zinc-700 font-['Outfit']">AI Outreach Sequence</h3>
                            <Button size="sm" onClick={generateOutreach} disabled={generating}
                                className="bg-red-500 hover:bg-red-600 text-white text-xs h-7" data-testid="generate-outreach-btn">
                                {generating ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : <Zap className="w-3 h-3 mr-1" />}
                                {generating ? 'Generating...' : outreach.length > 0 ? 'Regenerate' : 'Generate Sequence'}
                            </Button>
                        </div>

                        {outreach.length === 0 ? (
                            <div className="bg-zinc-50 rounded-lg p-6 text-center text-sm text-zinc-500">
                                <MessageSquare className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
                                Click "Generate Sequence" to create AI-powered outreach messages
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {outreach.map(step => (
                                    <div key={step.id} className={`rounded-xl border p-4 ${step.status === 'sent' ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-zinc-200'}`}
                                        data-testid={`outreach-step-${step.id}`}>
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-2">
                                                <Badge className={step.type === 'sms' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}>
                                                    {step.type === 'sms' ? <MessageSquare className="w-3 h-3 mr-1" /> : <Mail className="w-3 h-3 mr-1" />}
                                                    Day {step.day} — {step.type.toUpperCase()}
                                                </Badge>
                                                {step.status === 'sent' && <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />}
                                            </div>
                                            <div className="flex items-center gap-1">
                                                {step.status !== 'sent' && (
                                                    <>
                                                        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs"
                                                            onClick={() => { setEditStep(step.id); setEditMsg(step.message); }}>
                                                            <Edit3 className="w-3 h-3" />
                                                        </Button>
                                                        <Button size="sm" onClick={() => sendStep(step.id)} disabled={sending === step.id}
                                                            className="bg-emerald-600 hover:bg-emerald-700 text-white h-6 px-3 text-xs" data-testid={`send-step-${step.id}`}>
                                                            {sending === step.id ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                                                            Send
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        {step.subject && <div className="text-xs font-medium text-zinc-700 mb-1">Subject: {step.subject}</div>}
                                        {editStep === step.id ? (
                                            <div className="space-y-2">
                                                <Textarea value={editMsg} onChange={e => setEditMsg(e.target.value)} className="text-sm min-h-[80px]" />
                                                <div className="flex gap-2">
                                                    <Button size="sm" onClick={() => saveEdit(step.id)} className="bg-zinc-900 text-white text-xs h-6">Save</Button>
                                                    <Button size="sm" variant="ghost" onClick={() => setEditStep(null)} className="text-xs h-6">Cancel</Button>
                                                </div>
                                            </div>
                                        ) : (
                                            <p className="text-sm text-zinc-700 whitespace-pre-line">{step.message}</p>
                                        )}
                                        {step.sent_at && <div className="text-[10px] text-zinc-400 mt-2">Sent: {new Date(step.sent_at).toLocaleString()}</div>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function FactorBar({ label, score, detail, weight }) {
    const pct = Math.min((score || 0) / (parseFloat(weight) * 0.01) * 100 / 100, 100);
    return (
        <div className="bg-white rounded-lg border border-zinc-200 p-3">
            <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-zinc-600">{label} <span className="text-zinc-400">({weight})</span></span>
                <span className="font-semibold text-zinc-900">{(score || 0).toFixed(1)}</span>
            </div>
            <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                <div className="h-full bg-red-400 rounded-full transition-all" style={{ width: `${pct}%` }} />
            </div>
            <div className="text-[10px] text-zinc-400 mt-1">{detail}</div>
        </div>
    );
}
